import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import type { NativeGitReview } from '../native-desktop';

export type ReviewSource = 'changes' | 'pull-request';

/** A conversation's own changed files, and those another conversation may have changed too. */
type ConversationFiles = { own: readonly string[]; shared: readonly string[] };

/** The path a `git diff` file section is about (its destination). */
function sectionPath(header: string): string {
  const destination = header.lastIndexOf(' b/');
  return destination >= 0 ? header.slice(destination + 3) : header.slice('diff --git '.length);
}

/** The sections of a unified diff about `paths` only. */
export function diffForPaths(diff: string, paths: ReadonlySet<string>): string {
  const sections = diff.split(/\n(?=diff --git )/);
  return sections
    .filter((section) => {
      const end = section.indexOf('\n');
      const header = end === -1 ? section : section.slice(0, end);
      return header.startsWith('diff --git ') && paths.has(sectionPath(header));
    })
    .join('\n');
}

/** The `git status --short` lines about `paths` only. */
export function statusLinesForPaths(
  lines: readonly string[],
  paths: ReadonlySet<string>,
): string[] {
  return lines.filter((line) => {
    // The first line may have lost its leading space to a trim: the code is one or two letters.
    const path = line.replace(/^\s*\S{1,2}\s+/, '').trim();
    const renamed = path.split(' -> ');
    return paths.has(path) || paths.has(renamed.at(-1) ?? path);
  });
}

/** Every file a conversation changed, its shared ones included. */
export function conversationPaths(files: ConversationFiles): Set<string> {
  return new Set([...files.own, ...files.shared]);
}

/**
 * What discarding a file's working-tree changes asks first: more plainly when another
 * conversation may be working on it.
 */
export function discardConfirmation(path: string, files: ConversationFiles): string {
  if (files.shared.includes(path))
    return `Discard the changes in ${path}? Another conversation working in this folder may have changed it too: its changes would go as well.`;
  if (!files.own.includes(path))
    return `${path} was not changed by this conversation: another conversation, or you, may be working on it. Discard its changes anyway?`;
  return `Discard working tree changes in ${path}?`;
}

/** Splits git status output into non-empty trimmed lines. */
export function parseStatusLines(status: string | undefined): string[] {
  return status?.trim().split('\n').filter(Boolean) ?? [];
}

/** Combines the staged and working-tree diffs into a single diff text. */
export function combineWorkingDiff(review: NativeGitReview | null): string {
  return [review?.stagedDiff, review?.diff].filter(Boolean).join('\n');
}

/** Resolves which diff text to display for the selected review source. */
export function resolveReviewDiff(review: NativeGitReview | null, source: ReviewSource): string {
  return source === 'pull-request' ? (review?.pullRequestDiff ?? '') : combineWorkingDiff(review);
}

/** Resolves the label shown for the current review source. */
export function resolveReviewLabel(
  source: ReviewSource,
  pullRequest: PullRequestSummary | null,
): string {
  return source === 'pull-request' && pullRequest !== null
    ? `PR #${pullRequest.number}`
    : 'Uncommitted';
}
